import React, { FC, useState, useEffect } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'

export interface AutoThemeConfig {
  themeCount: number
  batchSize: number
  customInstructions: string
}

interface AutoThemeModalProps {
  open: boolean
  defaultThemeCount: number
  onCancel: () => void
  onSubmit: (config: AutoThemeConfig) => void
}

const AutoThemeModal: FC<AutoThemeModalProps> = ({
  open,
  defaultThemeCount,
  onCancel,
  onSubmit,
}) => {
  const [themeCount, setThemeCount] = useState(defaultThemeCount)
  const [batchSize, setBatchSize] = useState(10)
  const [customInstructions, setCustomInstructions] = useState('')

  useEffect(() => {
    if (open) {
      setThemeCount(defaultThemeCount)
      setBatchSize(10)
      setCustomInstructions('')
    }
  }, [open, defaultThemeCount])

  const handleSubmit = () => {
    onSubmit({
      themeCount,
      batchSize,
      customInstructions,
    })
  }

  const handleThemeCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10)
    if (!isNaN(value) && value > 0) {
      setThemeCount(value)
    } else if (e.target.value === '') {
      setThemeCount(0)
    }
  }

  const handleBatchSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10)
    if (!isNaN(value) && value > 0) {
      setBatchSize(value)
    } else if (e.target.value === '') {
      setBatchSize(0)
    }
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Auto Extract Themes</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Number of Themes"
            type="number"
            value={themeCount}
            onChange={handleThemeCountChange}
            fullWidth
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Batch Size"
            type="number"
            value={batchSize}
            onChange={handleBatchSizeChange}
            fullWidth
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Custom Instructions"
            multiline
            rows={4}
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
            fullWidth
            placeholder="Add any custom instructions for theme extraction..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={themeCount === 0 || batchSize === 0}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AutoThemeModal
